import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { motion, AnimatePresence } from 'framer-motion';

const Section = styled.section`
  position: relative;
  height: 100vh;
  min-height: 680px;
  overflow: hidden;
  background: #0A0A0A;

  @media (max-width: 768px) {
    height: auto;
    min-height: 100svh;
  }
`;

const GridOverlay = styled.div`
  position: absolute;
  inset: 0;
  background-image:
    linear-gradient(rgba(255,255,255,0.018) 1px, transparent 1px),
    linear-gradient(90deg, rgba(255,255,255,0.018) 1px, transparent 1px);
  background-size: 64px 64px;
  pointer-events: none;
  z-index: 0;
  mask-image: linear-gradient(to right, black 30%, transparent 75%);
`;

const RightBg = styled(motion.div)`
  position: absolute;
  right: 0;
  top: 0;
  bottom: 0;
  width: 52%;
  background: #0E0E0E;
  clip-path: polygon(14% 0%, 100% 0%, 100% 100%, 0% 100%);
  z-index: 1;

  @media (max-width: 768px) { display: none; }
`;

const OrangeGlow = styled.div`
  position: absolute;
  right: 8%;
  top: 50%;
  transform: translateY(-50%);
  width: 520px;
  height: 520px;
  background: radial-gradient(circle, rgba(244,121,32,0.16) 0%, transparent 65%);
  pointer-events: none;
  z-index: 2;

  @media (max-width: 768px) { display: none; }
`;

const LeftArea = styled.div`
  position: absolute;
  left: 0;
  top: 0;
  bottom: 0;
  width: 58%;
  display: flex;
  align-items: center;
  z-index: 4;

  @media (max-width: 768px) {
    position: relative;
    width: 100%;
    padding: 110px 1.5rem 2rem;
    align-items: flex-start;
  }
`;

const LeftInner = styled.div`
  padding-left: clamp(2rem, calc((100vw - 1400px) / 2 + 2rem), 8rem);
  padding-right: 3rem;
  display: flex;
  flex-direction: column;
  gap: 1.75rem;
  width: 100%;

  @media (max-width: 768px) {
    padding-left: 0;
    padding-right: 0;
  }
`;

const Badge = styled(motion.div)`
  display: inline-flex;
  align-items: center;
  gap: 0.75rem;
  width: fit-content;
`;

const BadgeLine = styled.div`
  width: 28px;
  height: 2px;
  background: #F47920;
  border-radius: 1px;
  flex-shrink: 0;
`;

const BadgeText = styled.span`
  font-size: 0.75rem;
  letter-spacing: 0.16em;
  text-transform: uppercase;
  color: #B3B3B3;
  font-weight: 500;
`;

const H1 = styled.h1`
  font-family: 'Barlow Condensed', sans-serif;
  font-size: clamp(3.8rem, 7vw, 7rem);
  font-weight: 900;
  line-height: 0.92;
  letter-spacing: -0.01em;
  text-transform: uppercase;
`;

const Word = styled(motion.span)`
  display: inline-block;
  margin-right: 0.14em;

  &.accent { color: #F47920; }
`;

const Divider = styled(motion.div)`
  width: 56px;
  height: 3px;
  background: linear-gradient(90deg, #F47920, transparent);
  border-radius: 2px;
`;

const Sub = styled(motion.p)`
  font-size: 1rem;
  color: #777;
  line-height: 1.8;
  max-width: 460px;
`;

const Btns = styled(motion.div)`
  display: flex;
  gap: 1rem;
  flex-wrap: wrap;
`;

const PrimaryBtn = styled(motion.a)`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  background: #F47920;
  color: #fff;
  padding: 0.9rem 2.2rem;
  border-radius: 8px;
  font-weight: 700;
  font-size: 0.88rem;
  letter-spacing: 0.07em;
  text-transform: uppercase;
  cursor: pointer;
  transition: background 0.2s ease, box-shadow 0.2s ease;

  &:hover {
    background: #FF8C3A;
    box-shadow: 0 8px 30px rgba(244,121,32,0.35);
  }
`;

const SecBtn = styled(motion.a)`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  border: 1px solid #2A2A2A;
  color: #888;
  padding: 0.9rem 2.2rem;
  border-radius: 8px;
  font-weight: 600;
  font-size: 0.88rem;
  letter-spacing: 0.07em;
  text-transform: uppercase;
  cursor: pointer;
  transition: border-color 0.2s ease, color 0.2s ease;

  &:hover {
    border-color: #F47920;
    color: #F47920;
  }
`;

const RightContent = styled.div`
  position: absolute;
  right: 0;
  top: 0;
  bottom: 0;
  width: 52%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  z-index: 3;
  padding-left: 10%;
  padding-bottom: 40px;
  gap: 2rem;

  @media (max-width: 768px) {
    position: relative;
    width: 100%;
    padding: 0 2rem 3rem;
    align-items: center;
  }
`;

const ProductWrap = styled(motion.div)`
  position: relative;
  width: 430px;
  height: 430px;

  @media (max-width: 1200px) {
    width: 340px;
    height: 340px;
  }

  @media (max-width: 768px) {
    width: min(320px, 78vw);
    height: min(320px, 78vw);
  }
`;

const ProductImg = styled(motion.img)`
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  object-fit: contain;
  filter: drop-shadow(0 30px 70px rgba(0,0,0,0.9));
  z-index: 1;
`;

const FloatingTag = styled(motion.div)`
  position: absolute;
  background: #161616;
  border: 1px solid #252525;
  border-radius: 12px;
  padding: 0.7rem 1rem;
  display: flex;
  align-items: center;
  gap: 0.75rem;
  z-index: 4;
  white-space: nowrap;
  box-shadow: 0 8px 32px rgba(0,0,0,0.5);
`;

const TagIcon = styled.div`
  width: 36px;
  height: 36px;
  background: rgba(244,121,32,0.12);
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #F47920;
  flex-shrink: 0;

  svg { width: 18px; height: 18px; }
`;

const TagInfo = styled.div`
  .label { font-size: 0.65rem; color: #555; text-transform: uppercase; letter-spacing: 0.08em; }
  .value { font-size: 0.9rem; font-weight: 700; color: #fff; }
`;

const OrangeBadge = styled(motion.div)`
  position: absolute;
  background: #161616;
  border: 1px solid #2A2A2A;
  color: #fff;
  border-radius: 10px;
  padding: 0.6rem 0.9rem;
  font-family: 'Barlow Condensed', sans-serif;
  font-size: 0.8rem;
  font-weight: 800;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  text-align: center;
  line-height: 1.25;
  z-index: 4;
  box-shadow: 0 8px 24px rgba(0,0,0,0.5);

  span { color: #F47920; }
`;

const CarouselFooter = styled(motion.div)`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.75rem;
`;

const ProductLabel = styled(motion.div)`
  font-family: 'Barlow Condensed', sans-serif;
  font-size: 0.85rem;
  font-weight: 700;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  color: #555;
`;

const Dots = styled.div`
  display: flex;
  gap: 6px;
  align-items: center;
`;

const Dot = styled.button`
  width: ${({ $active }) => $active ? '22px' : '6px'};
  height: 6px;
  border-radius: 3px;
  background: ${({ $active }) => $active ? '#F47920' : 'rgba(255,255,255,0.15)'};
  border: none;
  padding: 0;
  cursor: pointer;
  transition: all 0.35s ease;

  &:hover {
    background: ${({ $active }) => $active ? '#F47920' : 'rgba(255,255,255,0.3)'};
  }
`;

/* ─── Datos ─── */

const HERO_PRODUCTS = [
  { src: '/img/FOTOS FEREIMA/webp/Arrachera Premium.webp',             name: 'Arrachera Premium' },
  { src: '/img/FOTOS FEREIMA/webp/Rib eye con etiqueta.webp',          name: 'Rib Eye' },
  { src: '/img/FOTOS FEREIMA/webp/T bone supremo con etiqueta.webp',   name: 'T-Bone Supremo' },
  { src: '/img/FOTOS FEREIMA/webp/New York con etiqueta.webp',         name: 'New York' },
  { src: '/img/FOTOS FEREIMA/webp/Chuleton con etiqueta.webp',         name: 'Chuletón' },
  { src: '/img/FOTOS FEREIMA/webp/arrachera Marinada.webp',            name: 'Arrachera Marinada' },
  { src: '/img/FOTOS FEREIMA/webp/EE 5.webp',                          name: 'Empanizado Premium' },
];

const INTERVAL = 3200;

const imgVariants = {
  enter:  { opacity: 0, scale: 0.86 },
  center: { opacity: 1, scale: 1,    transition: { duration: 0.65, ease: [0.22, 1, 0.36, 1] } },
  exit:   { opacity: 0, scale: 1.06, transition: { duration: 0.4,  ease: 'easeIn' } },
};

const labelVariants = {
  enter:  { opacity: 0, y: 8 },
  center: { opacity: 1, y: 0, transition: { duration: 0.4, ease: 'easeOut' } },
  exit:   { opacity: 0, y: -8, transition: { duration: 0.25 } },
};

const WORDS = [
  { text: 'DEL',   accent: false },
  { text: 'CAMPO', accent: false },
  { text: 'A',     accent: false },
  { text: 'TU',    accent: false },
  { text: 'MESA',  accent: true  },
];

export default function Hero() {
  const [idx, setIdx] = useState(0);

  useEffect(() => {
    const t = setInterval(() => setIdx(i => (i + 1) % HERO_PRODUCTS.length), INTERVAL);
    return () => clearInterval(t);
  }, []);

  return (
    <Section id="inicio">
      <GridOverlay />

      <RightBg
        initial={{ x: '100%' }}
        animate={{ x: 0 }}
        transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
      />
      <OrangeGlow />

      {/* Contenido izquierdo */}
      <LeftArea>
        <LeftInner>
          <Badge
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.5, delay: 0.5 }}
          >
            <BadgeLine />
            <BadgeText>San Juan de los Lagos, Jalisco</BadgeText>
          </Badge>

          <H1>
            {WORDS.map((w, i) => (
              <Word
                key={w.text}
                className={w.accent ? 'accent' : ''}
                initial={{ opacity: 0, y: 50 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.55, delay: 0.6 + i * 0.08, ease: [0.22, 1, 0.36, 1] }}
              >
                {w.text}{' '}
              </Word>
            ))}
          </H1>

          <Divider
            initial={{ scaleX: 0, originX: 0 }}
            animate={{ scaleX: 1 }}
            transition={{ duration: 0.6, delay: 1.1 }}
          />

          <Sub
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 1.15 }}
          >
            Más de 20 años transformando esfuerzo y dedicación en cortes de la
            más alta calidad. Controlamos cada etapa: del rancho a tu mesa.
          </Sub>

          <Btns
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 1.3 }}
          >
            <PrimaryBtn href="#productos" whileHover={{ scale: 1.03 }} whileTap={{ scale: 0.97 }}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                <path d="M5 12h14M12 5l7 7-7 7"/>
              </svg>
              Ver Productos
            </PrimaryBtn>
            <SecBtn href="#nosotros" whileHover={{ scale: 1.03 }} whileTap={{ scale: 0.97 }}>
              Conoce más
            </SecBtn>
          </Btns>
        </LeftInner>
      </LeftArea>

      {/* Panel derecho — carrusel */}
      <RightContent>
        <ProductWrap
          initial={{ opacity: 0, scale: 0.8, rotate: -8 }}
          animate={{
            opacity: 1,
            scale: 1,
            rotate: 0,
            y: [0, -14, 0],
          }}
          transition={{
            opacity: { duration: 0.7, delay: 0.9 },
            scale:   { duration: 0.7, delay: 0.9, ease: [0.22, 1, 0.36, 1] },
            rotate:  { duration: 0.7, delay: 0.9, ease: [0.22, 1, 0.36, 1] },
            y:       { duration: 4.5, delay: 1.8, repeat: Infinity, ease: 'easeInOut' },
          }}
        >
          <AnimatePresence mode="wait">
            <ProductImg
              key={idx}
              src={HERO_PRODUCTS[idx].src}
              alt={`${HERO_PRODUCTS[idx].name} — Carnes Fereima`}
              variants={imgVariants}
              initial="enter"
              animate="center"
              exit="exit"
            />
          </AnimatePresence>

          <FloatingTag
            style={{ bottom: '10px', left: '-30px' }}
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.45, delay: 1.5 }}
          >
            <TagIcon>
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z"/>
              </svg>
            </TagIcon>
            <TagInfo>
              <div className="label">Máxima</div>
              <div className="value">Calidad</div>
            </TagInfo>
          </FloatingTag>

          <OrangeBadge
            style={{ top: '48px', right: '-18px' }}
            initial={{ opacity: 0, scale: 0.6 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.4, delay: 1.7, ease: [0.22, 1, 0.36, 1] }}
          >
            <span>+20</span> Años<br />Experiencia
          </OrangeBadge>
        </ProductWrap>

        {/* Nombre del producto + dots */}
        <CarouselFooter
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 2 }}
        >
          <AnimatePresence mode="wait">
            <ProductLabel
              key={idx}
              variants={labelVariants}
              initial="enter"
              animate="center"
              exit="exit"
            >
              {HERO_PRODUCTS[idx].name}
            </ProductLabel>
          </AnimatePresence>

          <Dots>
            {HERO_PRODUCTS.map((_, i) => (
              <Dot
                key={i}
                $active={i === idx}
                onClick={() => setIdx(i)}
                aria-label={HERO_PRODUCTS[i].name}
              />
            ))}
          </Dots>
        </CarouselFooter>
      </RightContent>

    </Section>
  );
}
